/**
=========================================================
* TripEnhancer - Image Resolver
=========================================================
*
* Resolves image URLs to use locally cached versions when available
* Falls back to original URLs if cache doesn't exist
*/

import imageCacheService from "./wikimediaService";

// Try to load image mapping (created during pre-deployment)
let imageMapping = {};
try {
  // This file is created by the predeploy script
  imageMapping = require("./imageMapping.json");
} catch (error) {
  // Mapping doesn't exist yet - will use original URLs
  console.log("ℹ️ Image mapping not found - using original URLs");
}

/**
 * Get the best available image URL
 * Priority:
 *   1. Locally cached image (from predeploy script)
 *   2. Client-side cached URL (from wikimediaService)
 *   3. Original URL
 *
 * @param {string} url - Original image URL
 * @param {number} width - Desired width (optional)
 * @returns {string} - Best available image URL
 */
export function resolveImageUrl(url, width = 1280) {
  if (!url) return url;

  // 1. Check if we have a locally cached version (from predeploy)
  if (imageMapping[url]) {
    console.log("✓ Using locally cached image:", imageMapping[url]);
    return imageMapping[url];
  }

  // 2. Use image cache service for client-side caching
  return imageCacheService.getOptimizedImageUrl(url, width);
}

/**
 * Preload an image
 * @param {string} url - Image URL
 * @returns {Promise<void>}
 */
export function preloadImage(url) {
  const resolvedUrl = resolveImageUrl(url);
  return imageCacheService.preloadImage(resolvedUrl);
}

/**
 * Check if an image is locally cached
 * @param {string} url - Original image URL
 * @returns {boolean}
 */
export function isLocallyCached(url) {
  return Object.prototype.hasOwnProperty.call(imageMapping, url);
}

/**
 * Get statistics about cached images
 * @returns {Object}
 */
export function getCacheStats() {
  return {
    locallyCached: Object.keys(imageMapping).length,
    hasLocalCache: Object.keys(imageMapping).length > 0,
    cacheType: Object.keys(imageMapping).length > 0 ? "local" : "client-side",
  };
}

export default {
  resolveImageUrl,
  preloadImage,
  isLocallyCached,
  getCacheStats,
};
